<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Reportes"></navbar-dashboard>
      <div class="ew-view">
        <page-header title="Reportes" subtitle="Consulta y exporta el detalle de envíos por periodo, paquetería y cliente.">
          <button class="btn btn-outline-secondary" @click="downloadExcel" :disabled="shipments.length === 0 || isLoading">
            <i class="bi bi-file-earmark-spreadsheet mr-1"></i> Descargar Excel
          </button>
        </page-header>

        <!-- Métricas -->
        <div class="ew-stats" v-if="!hasRole(['helpdesk', 'sales'])">
          <stat-card label="Envíos" icon="bi bi-box-seam" tone="navy" :loading="isLoading"
            :value="totalShipments.toLocaleString('es-MX')"></stat-card>
          <stat-card label="Gastos" icon="bi bi-cash-stack" tone="magenta" :loading="isLoading"
            :value="totalExpenses | money"></stat-card>
          <stat-card label="Prepagos pagados" icon="bi bi-check2-circle" tone="success" :loading="isLoading"
            :value="totalPrepaidsPaid | money"></stat-card>
          <stat-card label="Prepagos pendientes" icon="bi bi-hourglass-split" tone="warning" :loading="isLoading"
            :value="totalPrepaidsPending | money"></stat-card>
        </div>

        <!-- Filtros -->
        <form class="card ew-filters mb-3" @submit.prevent="getReport(true)">
          <div class="ew-report-filters">
            <div class="form-group" v-if="['superadmin', 'helpdesk', 'sales'].includes(user_rol)">
              <label for="client">Cliente</label>
              <select id="client" v-model="filters.client" class="form-control">
                <option value="">Todos</option>
                <option v-for="client in clients" :key="client._id" :value="client._id">
                  {{ client.company.internalClientName || client.firstName + ' ' + client.lastName }}
                </option>
              </select>
            </div>
            <div class="form-group" v-if="['superadmin', 'helpdesk', 'sales'].includes(user_rol)">
              <label for="hasGuide">Estado de guía</label>
              <select id="hasGuide" v-model="filters.hasGuide" class="form-control">
                <option :value="undefined">Todos</option>
                <option :value="true">Con guía generada</option>
                <option :value="false">Sin guía (borradores)</option>
              </select>
            </div>
            <div class="form-group">
              <label for="startDate">Desde</label>
              <input id="startDate" type="date" v-model="filters.startDate" class="form-control">
            </div>
            <div class="form-group">
              <label for="endDate">Hasta</label>
              <input id="endDate" type="date" v-model="filters.endDate" class="form-control">
            </div>
            <div class="form-group">
              <label for="carrier">Paquetería</label>
              <select id="carrier" v-model="filters.carrier" class="form-control">
                <option value="">Todas</option>
                <option v-for="c in carriers" :key="c" :value="c">{{ c | carrierName }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="shipmentType">Tipo de envío</label>
              <select id="shipmentType" v-model="filters.shipmentType" class="form-control">
                <option value="">Todos</option>
                <option v-for="t in shipmentTypes" :key="t" :value="t">{{ shipmentTypeLabels[t] || t }}</option>
              </select>
            </div>
            <div class="form-group ew-report-filters-actions">
              <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading">
                <i class="bi bi-funnel mr-1"></i> Generar reporte
              </button>
            </div>
          </div>
        </form>

        <b-modal ref="prepaidsModal" hide-footer title="Detalle de Prepagos">
          <div v-if="selectedPrepaids.length === 0" class="text-center text-muted">
            No hay prepagos
          </div>
          <ul v-else class="list-group">
            <li class="list-group-item" v-for="p in selectedPrepaids" :key="p._id">
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ p.type }} <small class="text-muted ml-1">{{ formatDate(p.created_at) }}</small></span>
                <span>
                  <strong class="ew-num">{{ p.amount | money }}</strong>
                  <span class="badge ml-2" :class="p.status ? 'badge-success' : 'badge-warning'">{{ p.status ? 'Pagado' : 'Pendiente' }}</span>
                </span>
              </div>
            </li>
          </ul>
          <div class="text-right mt-3">
            <button class="btn btn-outline-secondary" @click="$refs.prepaidsModal.hide()">Cerrar</button>
          </div>
        </b-modal>
        <b-modal id="shipment-detail-modal" size="lg" scrollable hide-footer header-class="ew-sm-header"
          body-class="ew-sm-body" @hidden="selectedShipment = null">
          <template #modal-header="{ close }">
            <div class="ew-sm-head" v-if="selectedShipment">
              <div class="ew-sm-head-main">
                <div class="ew-sm-eyebrow">Detalle del envío</div>
                <h5 class="ew-sm-title">{{ guideNumbers(selectedShipment)[0] || 'Sin guía' }}
                  <small v-if="guideNumbers(selectedShipment).length > 1" class="text-muted">
                    +{{ guideNumbers(selectedShipment).length - 1 }} guías</small>
                </h5>
                <div class="ew-sm-meta">
                  <tracking-status :status="getLastTrackingStatus(selectedShipment.tracking)"></tracking-status>
                  <span>Creado el {{ formatDate(selectedShipment.created_at) }}</span>
                  <span v-if="selectedShipment.user && selectedShipment.user.email">· {{ selectedShipment.user.email }}</span>
                </div>
              </div>
              <button type="button" class="close" aria-label="Cerrar" @click="close()">&times;</button>
            </div>
          </template>

          <div v-if="selectedShipment">
            <!-- Resumen -->
            <div class="ew-sm-summary">
              <div class="ew-sm-carrier">
                <img v-if="selectedShipment.selected_service && selectedShipment.selected_service.carrier_logo_url_svg"
                  :src="selectedShipment.selected_service.carrier_logo_url_svg" alt="" height="26">
                <div>
                  <div class="ew-sm-strong">{{ selectedShipment.selected_service && selectedShipment.selected_service.carrier | carrierName }}</div>
                  <div class="ew-sm-muted">{{ selectedShipment.selected_service && selectedShipment.selected_service.carrier_service_name | serviceName }}</div>
                </div>
              </div>
              <div>
                <div class="ew-sm-label">Tipo</div>
                <div class="ew-sm-strong">{{ shipmentTypeLabels[selectedShipment.shipment_type] || selectedShipment.shipment_type | dash }}</div>
              </div>
              <div v-if="!hasRole(['helpdesk', 'sales'])" class="text-right">
                <div class="ew-sm-label">Total</div>
                <div class="ew-sm-total">{{ selectedShipment.selected_service ? selectedShipment.selected_service.total_amount : null | money }}</div>
              </div>
            </div>

            <!-- Ruta -->
            <div class="ew-sm-route">
              <div v-for="side in [{ k: 'Origen', d: selectedShipment.origin_direction, i: 'bi-circle' }, { k: 'Destino', d: selectedShipment.destination_direction, i: 'bi-geo-alt-fill' }]"
                :key="side.k" class="ew-sm-party">
                <div class="ew-sm-label"><i class="bi mr-1" :class="side.i"></i>{{ side.k }}</div>
                <template v-if="side.d">
                  <div class="ew-sm-strong">{{ side.d.full_name || side.d.companyName | dash }}</div>
                  <div v-if="side.d.address" class="ew-sm-muted">{{ side.d.address }}</div>
                  <div class="ew-sm-muted">{{ formatPlace(side.d) }}</div>
                </template>
                <div v-else class="ew-sm-muted">—</div>
              </div>
            </div>

            <!-- Paquete y contenido -->
            <div class="ew-sm-grid">
              <div>
                <div class="ew-sm-section">Paquetes</div>
                <ul v-if="selectedShipment.parcels && selectedShipment.parcels.length" class="ew-sm-parcels">
                  <li v-for="(p, index) in selectedShipment.parcels" :key="index">
                    <span class="ew-sm-parcel-n">{{ index + 1 }}</span>
                    {{ p.width }} × {{ p.height }} × {{ p.depth }} cm
                    <span class="ml-auto ew-sm-strong">{{ p.weight }} kg</span>
                  </li>
                </ul>
                <div v-else class="ew-sm-muted">Sin paquetes registrados.</div>
              </div>
              <div>
                <div class="ew-sm-section">Contenido</div>
                <div class="ew-sm-text">{{ selectedShipment.description_of_the_content | dash }}</div>
                <div class="ew-sm-section mt-3">Seguro</div>
                <div class="ew-sm-text">
                  <template v-if="selectedShipment.package_insurance">
                    Sí · valor declarado {{ selectedShipment.insurance_information ? selectedShipment.insurance_information.package_value : null | money }}
                  </template>
                  <template v-else>Sin seguro</template>
                </div>
              </div>
            </div>

            <!-- Rastreo -->
            <div class="ew-sm-section">Historial de seguimiento</div>
            <ol v-if="selectedShipment.tracking && selectedShipment.tracking.length" class="ew-sm-timeline">
              <li v-for="(track, index) in trackingDesc(selectedShipment.tracking)" :key="index" :class="{ 'is-last': index === 0 }">
                <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 6px;">
                  <tracking-status :status="track.status || track.estado"></tracking-status>
                  <small class="text-muted">{{ formatDateTime(track.updated_at) }}</small>
                </div>
                <p v-if="trackingDescription(track.description, track.status || track.estado)" class="ew-sm-muted mb-0 mt-1">
                  {{ trackingDescription(track.description, track.status || track.estado) }}</p>
              </li>
            </ol>
            <div v-else class="ew-sm-empty"><i class="bi bi-clock-history mr-1"></i> Aún no hay movimientos de la paquetería.</div>
          </div>

          <div v-if="selectedShipment" class="ew-sm-footer">
            <router-link :to="'/shipments/' + selectedShipment._id" class="btn btn-outline-secondary">
              Ver detalle completo
            </router-link>
            <a v-if="selectedShipment.guide" :href="formatGuidePath(String(selectedShipment.guide).split(',')[0].trim())"
              target="_blank" rel="noopener" class="btn btn-primary">
              <i class="bi bi-file-earmark-pdf mr-1"></i> Descargar guía
            </a>
          </div>
        </b-modal>
        <!-- Tabla de resultados -->
        <div class="ew-table-scroll">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Guía</th>
                <th>Tipo</th>
                <th>Paquetería</th>
                <th>Último estatus</th>
                <th v-if="!hasRole(['helpdesk', 'sales'])" class="ew-num">Total</th>
                <th>Usuario</th>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Destino</th>
                <th>Contenido</th>
                <th>Seguro</th>
                <th v-if="!hasRole(['helpdesk', 'sales'])">Cargos adicionales</th>
                <th>Estado</th>
                <th v-if="!hasRole(['helpdesk', 'sales'])" class="text-center">Prepagos</th>
              </tr>
            </thead>
            <tbody v-if="isLoading">
              <tr v-for="n in 8" :key="'sk-' + n">
                <td v-for="c in columnCount" :key="'skc-' + c"><span class="ew-skeleton"></span></td>
              </tr>
            </tbody>
            <tbody v-else-if="shipments.length">
              <tr v-for="s in shipments" :key="s._id" class="ew-row-link" @click="openShipmentModal(s)">
                <td class="text-nowrap ew-guide">
                  <template v-if="s.number_guide">
                    <div v-for="(guide, index) in s.number_guide.split(',')" :key="index">{{ guide.trim() }}</div>
                  </template>
                  <span v-else class="text-muted">Sin guía</span>
                </td>
                <td class="text-nowrap">{{ shipmentTypeLabels[s.shipment_type] || s.shipment_type | dash }}</td>
                <td class="text-nowrap">
                  <template v-if="s.selected_service">
                    <div class="font-weight-bold">{{ s.selected_service.carrier | carrierName }}</div>
                    <small class="text-muted">{{ s.selected_service.carrier_service_name | serviceName }}</small>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="text-nowrap">
                  <tracking-status :status="getLastTrackingStatus(s.tracking)"></tracking-status>
                </td>
                <td v-if="!hasRole(['helpdesk', 'sales'])" class="ew-num">
                  {{ s.selected_service ? s.selected_service.total_amount : null | money }}
                </td>
                <td class="ew-user-cell" :title="s.user ? s.user.email : ''">{{ s.user && s.user.email | dash }}</td>
                <td class="text-nowrap">{{ s.created_at ? formatDate(s.created_at) : '—' }}</td>
                <td>
                  <div class="ew-party" :title="formatAddress(s.origin_direction)">
                    <span class="ew-party-name">{{ s.origin_direction && s.origin_direction.full_name | dash }}</span>
                    <span class="ew-party-place">{{ formatPlace(s.origin_direction) }}</span>
                  </div>
                </td>
                <td>
                  <div class="ew-party" :title="formatAddress(s.destination_direction)">
                    <span class="ew-party-name">{{ s.destination_direction && s.destination_direction.full_name | dash }}</span>
                    <span class="ew-party-place">{{ formatPlace(s.destination_direction) }}</span>
                  </div>
                </td>
                <td><div class="ew-clamp-2 ew-content-cell">{{ s.description_of_the_content | dash }}</div></td>
                <td class="text-nowrap">
                  <template v-if="s.package_insurance">
                    <span class="badge badge-success">Sí</span>
                    <small class="d-block text-muted mt-1">{{ s.insurance_information ? s.insurance_information.package_value : null | money }}</small>
                  </template>
                  <span v-else class="text-muted">No</span>
                </td>
                <td v-if="!hasRole(['helpdesk', 'sales'])" class="text-nowrap">
                  <template v-if="s.overweight !== null && s.overweight !== undefined && Number(s.overweight) > 0">
                    {{ s.overweight }} kg
                    <span class="badge ml-1" :class="s.overweight_paid ? 'badge-success' : 'badge-warning'">
                      {{ s.overweight_paid ? 'Pagado' : 'Pendiente' }}
                    </span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span class="badge" :class="s.status ? 'badge-success' : 'badge-secondary'">
                    {{ s.status ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>
                <td v-if="!hasRole(['helpdesk', 'sales'])" class="text-center">
                  <button class="btn btn-sm btn-outline-secondary ew-icon-btn"
                    @click.stop="showPrepaidsModal(s.prepaids ? s.prepaids : [])"
                    :disabled="!s.prepaids || s.prepaids.length === 0" v-b-tooltip.hover.top="'Ver prepagos'">
                    {{ s.prepaids ? s.prepaids.length : 0 }}
                  </button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td :colspan="columnCount" class="p-0">
                  <empty-state icon="bi bi-bar-chart" title="Sin resultados para este reporte"
                    text="Ajusta las fechas o los filtros y vuelve a generar el reporte."></empty-state>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <table-pagination :page="pagination.page" :total-pages="pagination.totalPages"
          :per-page="Number(pagination.limit)" :per-page-options="[20, 25, 50, 100]"
          @change="changePage" @per-page="setPerPage" />
      </div>
    </div>
  </div>

</template>

<script>
import axios from "axios";
import Navbar from './Header/Navbar'
import commons from '../commons';
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { roleMixin } from '@/mixins/roleMixin';
import { trackingDescription } from '@/utils/format';

export default {
  mixins: [roleMixin],
  components: {
    'navbar-dashboard': Navbar
  },
  data() {
    return {
      pagination: {
        page: 1,
        limit: 20,
        totalPages: 0
      },
      totalDocs: 0,
      selectedPrepaids: [],
      shipments: [],
      isLoading: false,
      filters: {
        startDate: '',
        endDate: '',
        shipmentType: '',
        carrier: '',
        client: "",
        hasGuide: undefined,
      },
      clients: [],
      carriers: [],
      shipmentTypes: ['document', 'box', 'tarima'],
      shipmentTypeLabels: { document: 'Sobre', box: 'Caja', tarima: 'Tarima' },
      user_rol: '',
      selectedShipment: null,
    };
  },
  created() {
    this.user_rol = localStorage.getItem('ew-user-role')
    // Por defecto: del inicio del mes actual al día de hoy
    const today = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.filters.startDate = iso(new Date(today.getFullYear(), today.getMonth(), 1));
    this.filters.endDate = iso(today);
  },
  methods: {
    getLastTrackingStatus(tracking) {
      if (!tracking || tracking.length === 0) return '-';
      const lastEntry = tracking[tracking.length - 1];
      return lastEntry.status || lastEntry.estado || '-';
    },
    formatGuidePath(path) {
      if (!path) return null;

      // 1. Origen actual (ej: https://app.dominio.com)
      const currentOrigin = window.location.origin;

      // 2. Ruta de la API
      const apiPath = '/api/public/';

      /**
       * 3. Regex para limpiar el prefijo:
       * ^(\.\/)? -> Busca opcionalmente un punto y una diagonal al inicio
       * src\/public\/ -> Busca la cadena exacta src/public/
       */
      const prefixRegex = /^(\.\/)?src\/public\//;

      if (prefixRegex.test(path)) {
        // 4. Reemplazamos el prefijo detectado por un string vacío para obtener solo el nombre del archivo
        const filename = path.replace(prefixRegex, '');

        // 5. Retornamos la URL construida
        const finalPath = currentOrigin + apiPath + filename;
        console.log("Formatted path:", finalPath);
        return finalPath;
      }

      // Si ya es una URL completa (http...) no hacemos nada
      return path;
    },
    openShipmentModal(shipment) {
      const url = `${commons.apiURL}/api/v1/shipments/${shipment._id}`
      axios.get(url)
        .then(res => {
          this.selectedShipment = { ...shipment, ...(res.data || {}) };
          this.$bvModal.show('shipment-detail-modal');
        })
        .catch(err => {
          console.error(err);
          this.selectedShipment = shipment;
          this.$bvModal.show('shipment-detail-modal');
        });
    },
    fetchCarrierFromShipment() {
      axios.get(`${commons.apiURL}/api/v1/carrier/getCarrierFromShipments`)
        .then(res => {
          this.carriers = res.data.map(item => item.cve);
        })
        .catch(err => console.error(err));
    },
    fetchClients() {
      axios.get(`${commons.apiURL}/api/v1/usersFilter?rol=client`)
        .then(res => {
          this.clients = res.data;
        })
        .catch(err => console.error(err));
    },
    showPrepaidsModal(prepaids) {
      this.selectedPrepaids = prepaids || [];
      this.$refs.prepaidsModal.show();
    },
    formatDate(date) {
      return new Date(date).toLocaleString(); // o tu función de formato
    },
    async downloadExcel() {
      this.isLoading = true;
      try {
        const payload = {
          ...this.filters
        };

        const response = await axios.post(
          `${commons.apiURL}/api/v1/reports/general/excel`,
          payload,
          { responseType: 'blob' }
        );

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'reporte_general_envios.xlsx');
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error("Error al descargar el Excel:", error);
      } finally {
        this.isLoading = false;
      }
    },
    /*downloadExcel() {
      const currentUserRole = localStorage.getItem("ew-user-role") || null;
      const data = this.shipments.map(s => ({
        Guía: s.number_guide || '-',
        Tipo: s.shipment_type || '-',

        // Validación de Carrier y Servicio
        Carrier: s.selected_service
          ? s.selected_service.carrier + ' (' + s.selected_service.carrier_service_name + ')'
          : '-',

        ...((currentUserRole !== 'helpdesk' && currentUserRole !== 'sales') ? {
          // Validación de montos del servicio
          Total: s.selected_service
            ? `${s.selected_service.total_amount} ${s.selected_service.currency}`
            : '-',
        } : {}),

        Usuario: (s.user && s.user.email) ? s.user.email : '-',
        Fecha: s.created_at ? this.formatDate(s.created_at) : '-',

        // Origen con validación de objeto
        Origen: s.origin_direction
          ? `${s.origin_direction.full_name}, ${s.origin_direction.address}, ${s.origin_direction.city}, ${s.origin_direction.state}`
          : '-',

        // Destino con validación de objeto
        Destino: s.destination_direction
          ? `${s.destination_direction.full_name}, ${s.destination_direction.address}, ${s.destination_direction.city}, ${s.destination_direction.state}`
          : '-',

        Contenido: s.description_of_the_content || '-',
        Seguro: s.package_insurance ? 'Sí' : 'No',

        // Valor del seguro (como pediste s.insurance_information ? ... : '-')
        ValorSeguro: s.insurance_information ? s.insurance_information.package_value : '-',

        Sobrepeso: (s.overweight !== null && s.overweight !== undefined) ? s.overweight : '-',

        ...((currentUserRole !== 'helpdesk' && currentUserRole !== 'sales') ? {
          CostoSobrepeso: (s.cost_overweight !== null && s.cost_overweight !== undefined) ? s.cost_overweight : '-',
        } : {}),

        SobrepesoPagado: s.overweight_paid ? 'Sí' : 'No',
        Estado: s.status ? 'Activo' : 'Inactivo',

        // Validación de array de prepagos
        Prepagos: (s.prepaids && s.prepaids.length > 0)
          ? s.prepaids.map(p => `${p.type || 'N/A'}: ${p.amount || 0} (${p.status ? 'Pagado' : 'Pendiente'})`).join('; ')
          : '-'
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ReporteEnvios');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'ReporteEnvios.xlsx');
    },*/

    // Export PDF
    downloadPDF() {
      const doc = new jsPDF()
      const columns = ['Guía', 'Tipo', 'Carrier', 'Total', 'Usuario', 'Fecha', 'Prepagos']
      const rows = this.shipments.map(s => [
        s.number_guide,
        s.shipment_type,
        s.selected_service.carrier,
        `${s.selected_service.total_amount} ${s.selected_service.currency}`,
        `${s.user.first_names} ${s.user.last_names} (${s.user == null ? '-' : s.user.email})`,
        this.formatDate(s.created_at),
        s.prepaids.map(p => `${p.type}: ${p.amount} (${p.status ? 'Pagado' : 'Pendiente'})`).join('; ')
      ])
      doc.autoTable({ head: [columns], body: rows, styles: { fontSize: 8 } })
      doc.save('ReporteEnvios.pdf')
    },
    async getReport(resetPage = false) {
      if (resetPage) this.pagination.page = 1;
      this.isLoading = true;
      try {
        const payload = {
          ...this.filters,
          page: this.pagination.page,
          limit: this.pagination.limit
        };
        const response = await axios.post(`${commons.apiURL}/api/v1/reports/general`, payload);

        this.shipments = response.data.data;
        this.pagination.totalPages = response.data.totalPages;
        this.totalDocs = response.data.totalDocs != null ? response.data.totalDocs : this.shipments.length;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    changePage(newPage) {
      this.pagination.page = newPage;
      this.getReport();
    },
    setPerPage(limit) {
      this.pagination.limit = limit;
      this.getReport(true);
    },
    trackingDescription,
    guideNumbers(s) {
      return s && s.number_guide ? String(s.number_guide).split(',').map(g => g.trim()).filter(Boolean) : [];
    },
    trackingDesc(tracking) {
      return [...(tracking || [])].reverse();
    },
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    formatPlace(dir) {
      if (!dir) return '';
      const city = [dir.city, dir.state_short || dir.state].filter(Boolean).join(', ');
      return dir.postal_code ? `${city} · C.P. ${dir.postal_code}` : city;
    },
    formatAddress(dir) {
      if (!dir) return '';
      return [dir.full_name, dir.address, dir.colony, dir.city, dir.state_short || dir.state,
        dir.postal_code ? `C.P. ${dir.postal_code}` : ''].filter(Boolean).join(', ');
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }
  },
  mounted() {
    if (!['superadmin', 'helpdesk', 'sales'].includes(this.user_rol)) {
      this.filters.client = localStorage.getItem('ew-user-parentClient');
    }
    this.getReport() // cargar reporte inicial
    this.fetchClients();
    this.fetchCarrierFromShipment();
  },
  computed: {
    // Total de envíos
    totalShipments() {
      return this.totalDocs
    },

    columnCount() {
      return this.hasRole(['helpdesk', 'sales']) ? 10 : 14
    },

    // Total de gastos (suma de total_amount de cada envío)
    totalExpenses() {
      return this.shipments.reduce((acc, s) => {
        const amount = parseFloat(s.selected_service ? s.selected_service.total_amount : 0)
        return acc + amount
      }, 0)
    },

    // Total de prepagos pagados
    totalPrepaidsPaid() {
      return this.shipments.reduce((acc, s) => {
        if (!s.prepaids) return acc
        const prepaidsPaid = s.prepaids
          .filter(p => p.status)
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        return acc + prepaidsPaid
      }, 0)
    },

    // Total de prepagos pendientes
    totalPrepaidsPending() {
      return this.shipments.reduce((acc, s) => {
        if (!s.prepaids) return acc
        const prepaidsPending = s.prepaids
          .filter(p => !p.status)
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        return acc + prepaidsPending
      }, 0)
    }
  }

};
</script>

<style scoped>
.ew-report-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0 12px;
  align-items: end;
}

.ew-guide {
  font-variant-numeric: tabular-nums;
}

.ew-user-cell {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Modal de detalle */
.ew-sm-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.ew-sm-head-main {
  flex: 1 1 auto;
  min-width: 0;
}

.ew-sm-eyebrow,
.ew-sm-label,
.ew-sm-section {
  color: #6b7185;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.ew-sm-title {
  margin: 2px 0 8px;
  color: #1c2030;
  font-size: 1.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ew-sm-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #6b7185;
  font-size: 0.8rem;
}

.ew-sm-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 24px;
  padding: 14px 16px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #f6f7fb;
}

.ew-sm-carrier {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ew-sm-strong {
  color: #1c2030;
  font-weight: 600;
}

.ew-sm-muted {
  color: #6b7185;
  font-size: 0.85rem;
}

.ew-sm-text {
  color: #2f3446;
  font-size: 0.875rem;
}

.ew-sm-total {
  color: #1c2030;
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ew-sm-route {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  margin-bottom: 20px;
  border: 1px solid #eceef5;
  border-radius: 10px;
}

.ew-sm-party {
  padding: 14px 16px;
  min-width: 0;
}

.ew-sm-party + .ew-sm-party {
  border-left: 1px solid #eceef5;
}

.ew-sm-party .ew-sm-label {
  margin-bottom: 6px;
}

.ew-sm-party .ew-sm-label i {
  color: #031d7d;
}

.ew-sm-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 20px;
}

.ew-sm-section {
  margin-bottom: 8px;
}

.ew-sm-parcels {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ew-sm-parcels li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  color: #2f3446;
  font-size: 0.875rem;
}

.ew-sm-parcels li + li {
  border-top: 1px solid #eceef5;
}

.ew-sm-parcel-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #e8ebf5;
  color: #031d7d;
  font-size: 0.72rem;
  font-weight: 700;
}

.ew-sm-timeline {
  margin: 0;
  padding: 0 0 0 18px;
  list-style: none;
  border-left: 2px solid #eceef5;
}

.ew-sm-timeline li {
  position: relative;
  padding: 0 0 16px 6px;
}

.ew-sm-timeline li:last-child {
  padding-bottom: 0;
}

.ew-sm-timeline li::before {
  content: "";
  position: absolute;
  top: 8px;
  left: -25px;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #c5c9d6;
  box-shadow: 0 0 0 1px #c5c9d6;
}

.ew-sm-timeline li.is-last::before {
  background: #031d7d;
  box-shadow: 0 0 0 1px #031d7d, 0 0 0 5px rgba(3, 29, 125, 0.12);
}

.ew-sm-empty {
  padding: 14px 16px;
  border: 1px dashed #dfe2ec;
  border-radius: 10px;
  color: #6b7185;
  font-size: 0.85rem;
}

.ew-sm-footer {
  position: sticky;
  bottom: -16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 20px -16px -16px;
  padding: 12px 16px;
  border-top: 1px solid #eceef5;
  background: #fff;
}

@media (max-width: 575.98px) {
  .ew-sm-summary,
  .ew-sm-route,
  .ew-sm-grid {
    grid-template-columns: 1fr;
  }

  .ew-sm-summary .text-right {
    text-align: left !important;
  }

  .ew-sm-party + .ew-sm-party {
    border-top: 1px solid #eceef5;
    border-left: 0;
  }
}

.ew-content-cell {
  min-width: 140px;
  max-width: 200px;
}
</style>



// WEBPACK FOOTER //
// src/components/Reports.vue